export enum DealDeskPriceType {
    list = "list",
    dealer = "dealer"
}

export enum ProcurementStage {
    quotation = "Quotation",
    negotiation = "Negotiation",
    finalDecision = "Final Decision"
}

export const PROCUREMENT_STAGES: ProcurementStage[] = [
    ProcurementStage.quotation,
    ProcurementStage.negotiation,
    ProcurementStage.finalDecision
];

// Stored as VARCHAR(50) rather than a postgres enum, so these are the exact
// stored values - see DAI.Database.Models.DealDeskStatus.
export enum DealDeskStatus {
    pending = "Pending",
    approved = "Approved",
    approvedWithChanges = "ApprovedWithChanges",
    rejected = "Rejected",
    moreInfoRequested = "MoreInfoRequested"
}

export const DEAL_DESK_STATUSES: DealDeskStatus[] = [
    DealDeskStatus.pending,
    DealDeskStatus.approved,
    DealDeskStatus.approvedWithChanges,
    DealDeskStatus.rejected,
    DealDeskStatus.moreInfoRequested
];

// Decisions a reviewer can record from an anonymous email link.
export const DEAL_DESK_DECISIONS: DealDeskStatus[] = [
    DealDeskStatus.approved,
    DealDeskStatus.approvedWithChanges,
    DealDeskStatus.rejected,
    DealDeskStatus.moreInfoRequested
];

// Decisions that only make sense if the reviewer also talks to the submitter.
export const DEAL_DESK_NEEDS_DISCUSSION: DealDeskStatus[] = [
    DealDeskStatus.approvedWithChanges,
    DealDeskStatus.moreInfoRequested
];

// Url friendly form used in the anonymous review links.
const SLUGS: Record<DealDeskStatus, string> = {
    [DealDeskStatus.pending]: "pending",
    [DealDeskStatus.approved]: "approved",
    [DealDeskStatus.approvedWithChanges]: "approved-with-changes",
    [DealDeskStatus.rejected]: "rejected",
    [DealDeskStatus.moreInfoRequested]: "more-info-requested"
};

// Human readable form used in emails and the review pages.
const LABELS: Record<DealDeskStatus, string> = {
    [DealDeskStatus.pending]: "Pending",
    [DealDeskStatus.approved]: "Approved",
    [DealDeskStatus.approvedWithChanges]: "Approved with Changes",
    [DealDeskStatus.rejected]: "Rejected",
    [DealDeskStatus.moreInfoRequested]: "More Info Requested"
};

// Reduces a value to its comparable form so the stored value, the url slug and
// the display label all resolve, in any casing.
function key(value: string | null | undefined): string | null {
    if (!value || !value.trim()) {
        return null;
    }
    return value.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
}

/**
 * Returns the stored form of a status, or null when the value is not one.
 * Accepts the stored value, the url slug and the display label, in any casing,
 * so "ApprovedWithChanges", "approved-with-changes" and "Approved with Changes"
 * all resolve.
 */
export function normalizeDealDeskStatus(status: string | null | undefined): DealDeskStatus | null {
    const target = key(status);
    return target === null
        ? null
        : DEAL_DESK_STATUSES.find(s => key(s) === target) ?? null;
}

export function dealDeskStatusSlug(status: string | null | undefined): string | null {
    const normalized = normalizeDealDeskStatus(status);
    return normalized === null ? null : SLUGS[normalized];
}

export function dealDeskStatusLabel(status: string | null | undefined): string {
    const normalized = normalizeDealDeskStatus(status);
    return normalized === null ? (status ?? '') : LABELS[normalized];
}
